// HelpPanel.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Services
import { subscribeToPublishedHelpArticles } from 'services/help/HelpArticleService';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
  HELP_ICON_PATH,
  filterHelpArticles,
  getHelpArticleTypeKeys,
  isHelpArticleUnscoped,
  normalizeHelpTypeKey,
} from 'utilities/HelpArticles';

// CSS
import 'admin/components/section/list/AdminSectionList.css';
import './HelpPanel.css';

// Components
import AdminSectionListRow from 'admin/components/section/list/row/AdminSectionListRow';
import Modal from 'components/modal/Modal';
import Pill from 'components/pill/Pill';
import RichTextRenderer from 'components/richtext/RichTextRenderer';
import TextInput from 'components/textinput/TextInput';

/**
 * HelpPanel
 *
 * In-app help reader. Ranks articles for the current type when one is open,
 * always includes unscoped articles, and searches the full library.
 *
 * @param {boolean} isOpen - Whether the panel is visible.
 * @param {function} onClose - Closes the panel.
 * @param {string} typeKey - Current node type key, if any.
 * @param {string} typeLabel - Current node type label.
 *
 * @returns {ReactNode}
 */
const HelpPanel = ({
  isOpen = false,
  onClose,
  typeKey = '',
  typeLabel = '',
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { getLabel } = useNodeTypes();
  const isMobile = useIsMobile();

  // ----- STATE ----- //

  const [query, setQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');

  // ----- MEMOS ----- //

  const cleanedTypeKey = normalizeHelpTypeKey(typeKey);
  const selectedArticle = useMemo(
    () => articles.find((article) => article.key === selectedKey) || null,
    [articles, selectedKey],
  );

  const visibleArticles = useMemo(() => (
    filterHelpArticles(articles, {
      query,
      scopedOnly: Boolean(cleanedTypeKey),
      typeKey: cleanedTypeKey,
    })
  ), [articles, cleanedTypeKey, query]);

  /**
   * Resolves the secondary line for an article row.
   *
   * @param {object} article
   * @returns {string}
   */
  const getArticleMeta = (article) => {
    const typeKeys = getHelpArticleTypeKeys(article);
    if (isHelpArticleUnscoped(article)) return 'All';
    if (cleanedTypeKey && typeKeys.includes(cleanedTypeKey)) {
      return Array.isArray(article?.tags) && article.tags.length
        ? article.tags.join(', ')
        : typeLabel;
    }
    return typeKeys
      .map((key) => getLabel(key) || key)
      .join(', ');
  };

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedKey('');
      setArticles([]);
      return undefined;
    }

    return subscribeToPublishedHelpArticles((rows) => {
      setArticles(Array.isArray(rows) ? rows : []);
    });
  }, [isOpen]);

  // ----- RENDER ----- //

  return (
    <Modal
      height={isMobile ? '100%' : '80vh'}
      iconKey="help"
      isOpen={isOpen}
      title={selectedArticle ? selectedArticle.title : (typeLabel ? `${typeLabel} help` : 'Help')}
      width={isMobile ? '100%' : '720px'}
      onBack={selectedArticle ? () => setSelectedKey('') : undefined}
      onClose={onClose}
    >
      {selectedArticle ? (
        <div className="help-panel-article">
          <RichTextRenderer value={selectedArticle.content} />
          {selectedArticle.tags.length ? (
            <div className="help-panel-tags">
              {selectedArticle.tags.map((tag) => (
                <Pill
                  key={tag}
                  color={theme.outlineMuted}
                  label={tag}
                  labelColor={theme.foreground}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="help-panel-list">
          <div className="help-panel-search">
            <TextInput
              placeholder="Search help…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          {visibleArticles.length === 0 ? (
            <div
              className="help-panel-empty"
              style={{
                color: theme.foregroundMuted,
              }}
            >
              {query.trim()
                ? 'No articles match that search.'
                : 'No help articles are published yet.'}
            </div>
          ) : (
            <div className="admin-section-list-container">
              {visibleArticles.map((article) => (
                <AdminSectionListRow
                  key={article.key}
                  iconKey={isHelpArticleUnscoped(article) ? 'apps' : 'help'}
                  iconPath={HELP_ICON_PATH}
                  isSelected={false}
                  metaText={getArticleMeta(article)}
                  theme={theme}
                  title={article.title || 'Untitled'}
                  onSelect={() => setSelectedKey(article.key)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

// Dev Tools
HelpPanel.displayName = 'HelpPanel';

export default HelpPanel;
